import { useEffect, RefObject } from "react";

function isEditable(el: Element | null): el is HTMLElement {
  if (!el) return false;

  if (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement
  ) {
    return true;
  }

  if ((el as HTMLElement).isContentEditable) return true;

  return false;
}

interface AutoBlurOptions {
  /** Container scope (recommended). If omitted, uses document */
  rootRef?: RefObject<HTMLElement | any>;
  /** Enable / disable without unmounting */
  enabled?: boolean;
}

/**
 * Blurs focused input when user taps/clicks outside it.
 * Good enough to mimic Chrome's “tap elsewhere to close keyboard”
 * without fighting Safari's keyboard/scroll behavior.
 */
export function useAutoBlurOnInteraction(options: AutoBlurOptions = {}) {
  const { rootRef, enabled = true } = options;

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    const root: EventTarget = rootRef?.current ?? document;

    const handlePointerDown = (e: Event) => {
      const active = document.activeElement as HTMLElement | null;
      if (!isEditable(active)) return;

      const target = e.target as Node | null;

      // If you tap inside the currently focused input, don't blur.
      if (target && active.contains(target)) return;

      active.blur();
    };

    // Tap / click anywhere inside `root`
    root.addEventListener("pointerdown", handlePointerDown, true);
    root.addEventListener("touchstart", handlePointerDown, true);
    root.addEventListener("mousedown", handlePointerDown, true);

    return () => {
      root.removeEventListener("pointerdown", handlePointerDown, true);
      root.removeEventListener("touchstart", handlePointerDown, true);
      root.removeEventListener("mousedown", handlePointerDown, true);
    };
  }, [rootRef, enabled]);
}
